import { icon } from "@fortawesome/fontawesome-svg-core";
import _ from "lodash";

export const isValuePresent = (value) => {
  return _.isNil(value) === false && value !== "";
};

export const parsedSpacingValue = (value) => {
  if (!isValuePresent(value)) return;
  if (String(value).includes("%")) return value;
  return value + "px";
};

export const formatSpacingValueInput = (value) => {
  let formattedValue = value.replace(/[^0-9+%-.]+/gi, "");

  if (typeof formattedValue === "string" && isValuePresent(formattedValue)) {
    formattedValue = String(parseFloat(formattedValue));
  }

  // Ensure only one percentage sign at the end
  const percentageIndex = value.indexOf("%");
  if (percentageIndex !== -1 && percentageIndex !== formattedValue.length - 1) {
    formattedValue = formattedValue.replace(/%/g, "") + "%";
  }

  // Ensure only one negative sign at the beginning
  const negativeIndex = formattedValue.indexOf("-");
  if (negativeIndex > 0) {
    formattedValue = formattedValue.replace(/-/g, "");
    formattedValue = "-" + formattedValue;
  }

  return formattedValue;
};

export const applyOpacityToColor = (
  color,
  opacity,
  override_existent_opacity = true
) => {
  if (!color) return color;

  const opacityDecimal = opacity / 100;

  if (color.startsWith("rgb(")) {
    return color.replace("rgb", "rgba").replace(")", `, ${opacityDecimal})`);
  } else if (color.startsWith("rgba(")) {
    if (override_existent_opacity == false) {
      const match = color.match(/rgba\([^)]+,\s*([\d.]+)\)/);

      if (match && match[1]) {
        const existingOpacity = parseFloat(match[1]);
        const newOpacity = existingOpacity * opacityDecimal;

        return color.replace(/[\d.]+\)$/, `${newOpacity})`);
      }
    }

    return color.replace(/[\d.]+\)$/, `${opacityDecimal})`);
  } else if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16) || 0;
    const g = parseInt(color.slice(3, 5), 16) || 0;
    const b = parseInt(color.slice(5, 7), 16) || 0;

    return `rgba(${r}, ${g}, ${b}, ${opacityDecimal})`;
  }

  return color;
};

const ROOT_PREFIX =
  /^(?:html|body|:root)(?![\w-])(?:\s*>?\s*(?:html|body|:root)(?![\w-]))*/;

const scopeSelector = (selector, scope) => {
  const sel = selector.trim();
  if (ROOT_PREFIX.test(sel)) {
    return sel.replace(ROOT_PREFIX, scope);
  }
  return `${scope} ${sel}`;
};

export const scopeCss = (css, scope) =>
  css
    .replace(/\/\*[\s\S]*?\*\//g, "")
    .replace(/([^{}]+)\{/g, (match, prelude) => {
      const selectors = prelude.trim();
      if (selectors.startsWith("@") || /^(from\b|to\b|\d+%)/.test(selectors)) {
        return match;
      }
      return (
        selectors
          .split(",")
          .map((selector) => scopeSelector(selector, scope))
          .join(", ") + " {"
      );
    });

// Confines the CSS inside a customer's <style> blocks to `scope` so it can't
// restyle the builder chrome. Only used on the builder canvas (props.builder).
export const scopeCustomCode = (html, scope) => {
  if (typeof html !== "string") return html;
  return html.replace(
    /<style\b[^>]*>([\s\S]*?)<\/style>/gi,
    (match, css) => `<style>${scopeCss(css, scope)}</style>`
  );
};

// Patches an element's live canvas preview in place. Shared by the settings
// copy editor's livePreview and the Remix "Replace" action so both write to the
// same '#builder .cf-element ... .froala-editor' node.
export const syncCanvasElementPreview = (objectId, previewSelector, html) => {
  if (!objectId || html === undefined || html === null) return;

  let selector = `#builder .cf-element[data-element-id="${objectId}"]`;
  if (previewSelector) selector += ` ${previewSelector}`;
  selector += " .froala-editor";

  const preview = document.querySelector(selector);
  if (preview) preview.innerHTML = html;
};

const LEGACY_COLUMN_NAMES = ["first", "second", "third", "fourth"];

// Single source of truth for a flex section's columns. Merges columns from
// both the section (server/persisted) and variant (live builder) sources,
// deduped by id with the variant copy winning, filtered to this section and
// excluding pending deletions, then sorted by position. This is a union — not
// an either/or — so a column present in only one source is never dropped, which
// keeps the canvas (Section.js) and the layers panel (SectionLayer) in agreement.
export const getFlexSectionColumns = (section, variant) => {
  const byId = new Map();
  [
    ...(section.section_columns ? Object.values(section.section_columns) : []),
    ...(variant && variant.section_columns
      ? Object.values(variant.section_columns)
      : []),
  ].forEach((sc) => byId.set(sc.id, sc));

  return Array.from(byId.values())
    .filter((sc) => sc.section_id == section.id && sc.toBeDeleted !== true)
    .sort((a, b) => a.position - b.position);
};

// Live builder columns for a flex section: the variant collection ONLY (no
// merge with the section's persisted snapshot, unlike getFlexSectionColumns).
// Use this where the count/order must reflect the latest builder edits — e.g.
// the Columns tab and the layout-preset picker — so a column added/duplicated
// in the session is counted immediately and a stale snapshot can't inflate it.
export const getLiveFlexSectionColumns = (section, variant) =>
  Object.values((variant && variant.section_columns) || {})
    .filter((sc) => sc.section_id == section.id && sc.toBeDeleted !== true)
    .sort((a, b) => a.position - b.position);

export const flexRowBreakEvery = (section, widthKey) => {
  const structure = (section.options || {})["structure"] || {};
  const parsePerRow = (key) => {
    const n = parseInt(structure[key], 10);
    return !Number.isNaN(n) && n > 0 ? n : null;
  };
  const desktop = parsePerRow("columns-per-row");
  if (widthKey !== "width-mobile") return desktop;

  const mobileResponsive = structure["mobile-responsive"] !== "false";
  return (
    parsePerRow("columns-per-row-mobile") || (mobileResponsive ? 1 : desktop)
  );
};

export const flexSiblingWidthClearUpdates = (
  section,
  variant,
  editedColumnId,
  widthKey
) => {
  const columns = getLiveFlexSectionColumns(section, variant);
  const editedIndex = columns.findIndex(
    (column) => column.id === editedColumnId
  );
  if (editedIndex === -1) return [];

  const breakEvery = flexRowBreakEvery(section, widthKey);
  const rowOf = (index) => (breakEvery ? Math.floor(index / breakEvery) : 0);
  const editedRow = rowOf(editedIndex);

  return columns
    .filter(
      (column, index) =>
        column.id !== editedColumnId &&
        rowOf(index) === editedRow &&
        !(column.options || {})[`${widthKey}_locked`]
    )
    .map((column) => ({
      object_type: "section_columns",
      object_id: column.id,
      setting_name: `[options][${widthKey}]`,
      value: "",
    }));
};

export const flexWidthResetUpdates = (columns, widthKey, valueFor) =>
  columns.flatMap((column, index) => {
    const updates = [
      {
        object_type: "section_columns",
        object_id: column.id,
        setting_name: `[options][${widthKey}]`,
        value: valueFor(column, index),
      },
    ];
    if ((column.options || {})[`${widthKey}_locked`]) {
      updates.push({
        object_type: "section_columns",
        object_id: column.id,
        setting_name: `[options][${widthKey}_locked]`,
        value: "",
      });
    }
    return updates;
  });

const flexColumnLayoutContext = (section, variant, device) => {
  const isMobile = device === "mobile";
  return {
    widthKey: isMobile ? "width-mobile" : "width",
    perRowKey: isMobile ? "columns-per-row-mobile" : "columns-per-row",
    columns: getLiveFlexSectionColumns(section, variant),
  };
};

export const flexColumnLayoutCanReset = (section, variant, device) => {
  const { widthKey, perRowKey, columns } = flexColumnLayoutContext(
    section,
    variant,
    device
  );
  if (flexColumnLayoutPresets(columns.length).length === 0) return false;

  const hasWidthOverride = columns.some((column) => {
    const width = column.options && column.options[widthKey];
    return width !== undefined && width !== null && width !== "";
  });
  const perRowValue =
    section.options &&
    section.options.structure &&
    section.options.structure[perRowKey];
  const hasPerRow =
    perRowValue !== undefined && perRowValue !== null && perRowValue !== "";

  return hasWidthOverride || hasPerRow;
};

export const flexColumnLayoutResetUpdates = (section, variant, device) => {
  const { widthKey, perRowKey, columns } = flexColumnLayoutContext(
    section,
    variant,
    device
  );
  const updates = flexWidthResetUpdates(columns, widthKey, () => "");
  updates.push({
    object_type: "sections",
    object_id: section.id,
    setting_name: `[options][structure][${perRowKey}]`,
    value: "",
  });
  return updates;
};

// Maps resolved section_columns to the lightweight column descriptors the
// renderer uses. Exposed so callers that already hold the columns (e.g.
// Section.js) can reuse them instead of resolving them a second time.
export const toFlexColumnDescriptors = (sectionColumns) =>
  sectionColumns.map((sc, idx) => ({
    name: LEGACY_COLUMN_NAMES[idx] || `col-${sc.id}`,
    position: sc.position,
    section_column_id: sc.id,
  }));

export const getFlexColumns = (section, variant) =>
  toFlexColumnDescriptors(getFlexSectionColumns(section, variant));

// Coerce a user-entered width override into a CSS length. Accepts "400px",
// "60%", or a bare number (treated as px). Returns null when empty/invalid.
export const normalizeFlexWidthValue = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  if (str === "") return null;
  if (str.endsWith("%") || str.endsWith("px")) return str;
  const num = parseFloat(str);
  if (Number.isNaN(num)) return null;
  return `${num}px`;
};

// Resolves a flex column's sizing for desktop and mobile, returning
// `{ value, grow }` per device:
//  - grow: false + value => a fixed column with an explicit px/% width; it takes
//    exactly `value`.
//  - grow: true => an "auto" column; it grows to divide the space left by the
//    fixed columns in its row equally (flex: 1 1 0). So [10%, auto] => auto fills
//    the remaining 90%; [auto, auto] => 50/50; [10%, 10%] => both fixed at 10%.
// columns-per-row does NOT size columns — it only controls how many share a row
// (enforced by the row breaks in Section.js); within a row, auto columns grow.
// Mobile inherits the resolved desktop sizing unless a mobile width is set.
const FIXED = (value) => ({ value, grow: false });
const AUTO = { value: null, grow: true };

export const flexColumnWidthBasis = (columnStructure = {}) => {
  const override = normalizeFlexWidthValue(columnStructure["width"]);
  const desktop = override ? FIXED(override) : AUTO;

  const mobileOverride = normalizeFlexWidthValue(
    columnStructure["width-mobile"]
  );
  const mobile = mobileOverride ? FIXED(mobileOverride) : desktop;

  return { desktop, mobile };
};

// Curated NON-equal flex layouts per column count (the equal splits and the
// lower per-row counts are generated separately). Arrays are exact percentages
// that already sum to 100.
const FLEX_COLUMN_ALTERNATE_SHARES = {
  2: [
    [60, 40],
    [40, 60],
    [70, 30],
    [30, 70],
    [80, 20],
    [20, 80],
  ],
  3: [
    [50, 25, 25],
    [25, 50, 25],
    [25, 25, 50],
  ],
  4: [
    [40, 20, 20, 20],
    [35, 15, 15, 35],
  ],
};

const FLEX_COLUMN_MAX = 6;

// Returns the layout presets for a given column count: [{ key, label, values,
// perRow, isEqual }]. Includes an equal-division option for every per-row count
// from 1..count (e.g. 3 columns => "100" [1 per row], "50/50" [2 per row],
// "33/33/33" [3 per row]) so the column overflow/wrap layout can be controlled,
// plus the curated non-equal alternates for the full column count. Empty array
// when count < 1 or > 6.
export const flexColumnLayoutPresets = (count) => {
  if (!count || count < 1 || count > FLEX_COLUMN_MAX) return [];

  const equalPresets = [];
  for (let perRow = 1; perRow <= count; perRow++) {
    // Equal split per row with last-column-remainder so each full row sums to
    // exactly 100 (e.g. 6 per row => 16.67×5 + 16.65, never 100.02 which would
    // overflow and wrap the last column).
    const base = Math.round((100 / perRow) * 100) / 100;
    const remainder = Math.round((100 - base * (perRow - 1)) * 100) / 100;
    const shareAt = (index) =>
      index % perRow === perRow - 1 ? remainder : base;

    equalPresets.push({
      key: `equal-${perRow}`,
      label: Array.from({ length: perRow }, () =>
        Math.round(100 / perRow)
      ).join("/"),
      values: Array.from({ length: count }, (_, i) => shareAt(i)),
      // One representative row for the button diagram: `perRow` equal boxes
      // (e.g. "100" => one box, "50/50" => two), so it mirrors how the columns
      // will actually wrap rather than always showing every column.
      preview: Array.from({ length: perRow }, (_, i) => shareAt(i)),
      perRow,
      isEqual: perRow === count,
    });
  }

  const alternatePresets = (FLEX_COLUMN_ALTERNATE_SHARES[count] || []).map(
    (values) => ({
      key: values.join("-"),
      label: values.join("-"),
      values,
      preview: values,
      perRow: count,
      isEqual: false,
    })
  );

  return [...equalPresets, ...alternatePresets];
};

// Given the columns' explicit width values in order (e.g. ["70%", "30%"]),
// returns the key of the matching preset, or null. Auto/empty/non-% widths
// never match (a preset is only "active" when every column has an explicit %).
export const flexPresetMatchingWidths = (widthValues = []) => {
  if (
    !widthValues.length ||
    widthValues.some((w) => !w || !String(w).includes("%"))
  ) {
    return null;
  }
  const nums = widthValues.map((w) => parseFloat(w));
  const match = flexColumnLayoutPresets(nums.length).find(
    (preset) =>
      preset.values.length === nums.length &&
      preset.values.every((v, i) => Math.abs(v - nums[i]) < 0.5)
  );
  return match ? match.key : null;
};

export const isSectionColumnsMobileSorted = (section) => {
  // sortableJS sets all values even if the section only has 2 columns
  // check if every value is present and return true if so

  let column_positions = ["first", "second", "third", "fourth"];

  let is_mobile_sorted = column_positions
    .map((a) => {
      let position = _.get(
        section,
        `options.structure.cf-column-${a}-mobile-position`
      );
      return isValuePresent(position);
    })
    .every(Boolean);

  return is_mobile_sorted;
};

export const getWebsiteIntegration = (website, integration_name) => {
  return Object.values(website?.integrations).filter(
    (i) => i.integration_type == integration_name && i.status == true
  )[0];
};

export const normalizeObjectsPositions = (objects) => {
  if (!objects || objects.length === 0) return objects;

  const sortedObjects = _.sortBy(objects, ["position"]);

  return sortedObjects.map((object, index) => {
    return { ...object, position: index + 1 };
  });
};

export const closeBodyModalOpen = () => {
  setTimeout(() => {
    if ($(".modal.in").length < 1) {
      $("body").removeClass("modal-open");
    }
  }, 300);
};

export const convertIconToSvg = (iconClass) => {
  if (!iconClass || typeof iconClass !== "string") return "";

  try {
    const parts = iconClass.trim().split(/\s+/);
    let prefix = "fas";
    let iconName = "";

    if (parts.length === 2) {
      prefix = parts[0]; // fas, far, or fab
      iconName = parts[1].replace("fa-", ""); // remove 'fa-' prefix if present
    } else if (parts.length === 1) {
      iconName = parts[0].replace("fa-", "");
    } else {
      return "";
    }

    const iconDefinition = icon({ prefix, iconName });

    if (iconDefinition && iconDefinition.html && iconDefinition.html[0]) {
      return iconDefinition.html[0];
    }

    return "";
  } catch (error) {
    console.warn("Failed to convert icon to SVG:", iconClass, error);
    return "";
  }
};

export const isProductHeroBuiltinContentVisible = (element, key) => {
  let show = element.options[`${key}-show`];

  if (
    key == "quantity-picker" &&
    element.options["quantity-picker-show"] == undefined
  ) {
    show = "false";
  }
  if (
    key == "subscription" &&
    element.options["subscription-show"] == undefined
  ) {
    show = "false";
  }
  if (
    key == "reviews" &&
    (element.options["reviews-show"] == undefined ||
      element.options["products-source"] !== "ShopifyGraphql")
  ) {
    show = "false";
  }

  return show !== "false";
};

// A row element's inner section for the given parent section's step. Rows can
// carry a stale empty second inner section (duplication mirrors it), so prefer
// the one that actually holds content instead of a blind first match.
export const rowInnerSection = (element, parentSection) => {
  const candidates = Object.values(element.sections || {}).filter(
    (section) =>
      section.element_id == element.id &&
      section.step_id == parentSection.step_id
  );

  return (
    candidates.find(
      (section) => Object.keys(section.elements || {}).length > 0
    ) || candidates[0]
  );
};

export const hasPendingDuplicate = (variant, object_type, object_id) =>
  `${object_id}`.startsWith("temp-") ||
  Boolean(variant?.[object_type]?.[`temp-${object_id}`]);
